import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ClockCard } from "@/components/worker/ClockCard";
import { api } from "@/lib/api";
import { fmtDuration15, fmtTime15, fmtDate, fmtTime } from "@/lib/format";

const TWO_WEEKS_MS = 14 * 24 * 60 * 60 * 1000;

export const TimesheetTab = () => {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try { setShifts(await api.shifts()); }
    catch { toast.error("Failed to load timesheet"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>;

  // Only keep the last 2 weeks visible in the recent-shifts list.
  const cutoff = Date.now() - TWO_WEEKS_MS;
  const recent = shifts.filter((s) => s.clock_in && new Date(s.clock_in).getTime() >= cutoff);

  return (
    <div className="fade-up">
      <h2 className="font-display text-2xl font-bold mb-1">Timesheet</h2>
      <p className="text-sm text-muted-foreground mb-6">Clock on when you start your day.</p>

      <ClockCard />

      <h3 className="font-display text-lg font-semibold mb-3">Recent shifts <span className="text-sm font-normal text-muted-foreground">· last 2 weeks</span></h3>
      {recent.length === 0 ? (
        <p className="text-sm text-muted-foreground">No shifts in the last 2 weeks.</p>
      ) : (
        <div className="bg-card border border-border rounded-2xl overflow-x-auto">
          <Table className="min-w-[520px]">
            <TableHeader><TableRow>
              <TableHead>Date</TableHead><TableHead>In</TableHead><TableHead>Out</TableHead>
              <TableHead className="text-right">Break</TableHead><TableHead className="text-right">Worked</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {recent.map((s) => (
                <TableRow key={s.id} data-testid={`shift-row-${s.id}`}>
                  <TableCell className="text-muted-foreground">{fmtDate(s.clock_in)}</TableCell>
                  <TableCell className="font-mono text-sm">
                    <div>{fmtTime15(s.clock_in)}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">{fmtTime(s.clock_in)}</div>
                  </TableCell>
                  <TableCell className="font-mono text-sm">
                    {s.clock_out ? (
                      <>
                        <div>{fmtTime15(s.clock_out)}</div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">{fmtTime(s.clock_out)}</div>
                      </>
                    ) : "—"}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">{fmtDuration15(s.break_seconds)}</TableCell>
                  <TableCell className="text-right font-mono text-sm font-semibold">{fmtDuration15(s.worked_seconds)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {recent.length > 0 && (
        <p className="text-xs text-muted-foreground mt-2" data-testid="rounding-note">Break and worked times are rounded to the nearest 15 minutes.</p>
      )}
    </div>
  );
};
